// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useState, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { Eye, EyeOff, Leaf, Trash2, ArrowRight } from 'lucide-react'
import { Button, Input } from '@/components/ui'
import { useAuthStore }  from '@/store'
import { loadProfile, getFirstProfile, profileExists } from '@/services/profile'
import { ROUTES, HOME_ROUTE } from '@/lib/constants/routes'
import { DEMO_MODE } from '@/config/demo'
import { DemoResetButton } from '@/components/demo/DemoResetButton'

// ── Demo-reset helper (dezelfde logica als DemoResetButton) ───────────────────

async function fullDemoReset(): Promise<void> {
  await new Promise<void>((resolve) => {
    const req = indexedDB.deleteDatabase('WilsAppDB')
    req.onsuccess = () => resolve()
    req.onerror   = () => resolve()   // ook bij fout doorgaan
    req.onblocked = () => resolve()
  })
  Object.keys(localStorage) .filter(k => k.startsWith('wilsapp')).forEach(k => localStorage.removeItem(k))
  Object.keys(sessionStorage).filter(k => k.startsWith('wilsapp')).forEach(k => sessionStorage.removeItem(k))
  window.location.href = '/demo/'
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LoginPage() {
  const navigate             = useNavigate()
  const location             = useLocation()
  const { setAuthenticated } = useAuthStore()

  const [password,       setPassword]       = useState('')
  const [showPassword,   setShowPassword]   = useState(false)
  const [isLoading,      setIsLoading]      = useState(false)
  const [loginFailed,    setLoginFailed]    = useState(false)   // DEMO: oud profiel met ander wachtwoord
  const [error,          setError]          = useState<string | null>(null)
  const [hasProfile,     setHasProfile]     = useState<boolean | null>(null)
  const [savedUserId,    setSavedUserId]    = useState<string | null>(null)
  const [firstName,      setFirstName]      = useState<string>('')
  const [resetting,      setResetting]      = useState(false)
  const [confirmReset,   setConfirmReset]   = useState(false)

  const from = (location.state as { from?: Location })?.from?.pathname ?? null

  useEffect(() => {
    async function checkProfile() {
      const exists = await profileExists()
      setHasProfile(exists)
      if (exists) {
        const profile = await getFirstProfile()
        if (profile) {
          setSavedUserId(profile.id)
          setFirstName(profile.firstName)
        }
      }
    }
    checkProfile()
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!savedUserId || !password) return

    setIsLoading(true)
    setError(null)
    setLoginFailed(false)

    const result = await loadProfile(savedUserId, password)
    if (result.succes) {
      setAuthenticated(savedUserId)
      navigate(from ?? HOME_ROUTE, { replace: true })
    } else {
      if (DEMO_MODE) {
        // In DEMO_MODE: toon geen generieke fout maar directe reset-optie
        setLoginFailed(true)
      } else {
        setError(result.error?.message ?? 'Inloggen mislukt. Controleer uw wachtwoord.')
      }
      setIsLoading(false)
    }
  }

  async function handleDemoReset() {
    setResetting(true)
    await fullDemoReset()
  }

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (hasProfile === null) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-6 w-6 animate-pulse-soft rounded-full bg-sage-200" />
      </div>
    )
  }

  // ── Geen profiel - naar onboarding ───────────────────────────────────────────
  if (!hasProfile) {
    return (
      <div className="flex flex-col gap-0 overflow-hidden rounded-2xl shadow-soft border border-sand-300">

        {/* Hero - groen blok */}
        <div className="bg-moss px-6 pt-7 pb-8">
          <div className="flex items-center gap-2 mb-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-white/20">
              <Leaf className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-medium text-white/90">WilsApp</span>
          </div>
          <h1 className="text-xl font-semibold text-white leading-snug mb-3">
            WilsApp begeleidt u stap voor stap.
          </h1>
          <p className="text-sm text-white/80 leading-relaxed">
            WilsApp helpt u om belangrijke zaken rustig en stap voor stap vast te leggen,
            zolang u daar zelf nog wilsbekwaam toe bent.
          </p>
          <p className="text-sm text-white/80 leading-relaxed mt-2">
            Van persoonlijke wensen en medische keuzes tot financiële en praktische zaken:
            u houdt zelf de regie, zodat uw naasten later precies weten wat u belangrijk vindt.
          </p>
        </div>

        {/* Waarmee WilsApp u helpt */}
        <div className="bg-white px-6 pt-5">
          {/* Besloten pilot - rustige meldingsbalk */}
          <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3 mb-4">
            <span className="text-base flex-shrink-0" aria-hidden="true">🧪</span>
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold text-ink">Besloten pilot</p>
              <p className="text-xs text-ink-3 leading-relaxed">
                Deze versie van WilsApp is uitsluitend beschikbaar voor genodigde testers. De applicatie is nog in ontwikkeling. Uw ervaringen en feedback helpen om WilsApp verder te verbeteren.
              </p>
            </div>
          </div>
          <p className="text-sm font-semibold text-ink mb-3">
            Waarmee WilsApp u helpt:
          </p>
          <div className="grid grid-cols-2 gap-2 mb-4">
            {([
              { emoji: '🧭', text: 'Levensgebeurtenissen', sub: 'Voor als het leven verandert.' },
              { emoji: '✍️', text: 'Uw wensen rustig vastleggen' },
              { emoji: '🗂️', text: 'Niets vergeten, alles op één plek' },
              { emoji: '🤝', text: 'Uw naasten later wegwijs maken' },
            ] as { emoji: string; text: string; sub?: string }[]).map(item => (
              <div key={item.text} className="flex flex-col items-center text-center rounded-xl bg-sand px-3 py-3 gap-1.5">
                <span className="text-2xl">{item.emoji}</span>
                <span className="text-xs text-ink-3 leading-tight">{item.text}</span>
                {item.sub && <span className="text-xs text-ink-3 leading-tight">{item.sub}</span>}
              </div>
            ))}
          </div>

          {/* Geruststelling */}
          <div className="flex items-start gap-2.5 rounded-xl bg-sage-50 border border-sage-200 px-3 py-2.5 mb-4">
            <span className="text-base flex-shrink-0">✅</span>
            <p className="text-xs text-sage-800 leading-relaxed">
              U houdt zelf de regie. Als uw situatie verandert door ouder worden, ziekte of overlijden, geeft deze voorbereiding rust voor uzelf én uw naasten.
            </p>
          </div>

          {/* Drie checks */}
          <div className="flex flex-col gap-2 mb-5">
            {[
              { emoji: '⏱️', text: 'Binnen 10 tot 15 minuten een eerste overzicht' },
              { emoji: '📱', text: 'Uw gegevens blijven op uw eigen apparaat' },
              { emoji: '🔄', text: 'U hoeft niet alles in één keer te regelen; u kunt altijd later verdergaan' },
            ].map(item => (
              <div key={item.text} className="flex items-center gap-2.5">
                <span className="text-base flex-shrink-0">{item.emoji}</span>
                <span className="text-sm text-ink-3">{item.text}</span>
              </div>
            ))}
          </div>

          {/* Drie CTA's - rolkeuze */}
          <div className="flex flex-col gap-2.5 mb-5">
            <Button fullWidth onClick={() => navigate(ROUTES.ONBOARDING)}>
              Begin met voorbereiden
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <Button fullWidth variant="secondary" onClick={() => navigate(ROUTES.DEMO_PREVIEW)}>
              🎬 Demo - kijk hoe WilsApp werkt
            </Button>
            <button
              onClick={() => navigate(ROUTES.NEXT_OF_KIN_ENTRY)}
              className="flex items-center justify-center gap-2 w-full rounded-2xl border-2 border-sand-300 bg-white px-5 py-3.5 text-sm font-medium text-ink-3 hover:border-sage-300 hover:text-ink-2 transition-all"
            >
              <span className="text-base">🤝</span>
              Ik ben vertrouwenspersoon
            </button>
          </div>

          <p className="text-xs text-ink-4 text-center leading-relaxed pb-5">
            WilsApp vervangt geen testament, levenstestament of juridisch advies.<br />
            De app helpt u om belangrijke zaken overzichtelijk vast te leggen en ondersteunt u
            stap voor stap, zodat u niets over het hoofd ziet.
          </p>
        </div>

        <div className="bg-white px-6 pb-4 border-t border-sand-200">
          <DemoResetButton />
        </div>
      </div>
    )
  }

  // ── DEMO_MODE: inloggen mislukt - eenvoudige reset zonder technische uitleg ────
  if (DEMO_MODE && loginFailed) {
    return (
      <div className="bg-white rounded-2xl shadow-soft border border-sand-300 p-8 flex flex-col gap-5 text-center">
        <div className="flex justify-center mb-1">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-moss">
            <Leaf className="h-7 w-7 text-white" />
          </div>
        </div>
        <div>
          <h1 className="text-xl font-semibold text-ink mb-2">Opnieuw beginnen</h1>
          <p className="text-sm text-ink-3 leading-relaxed">
            De demo kan niet worden geopend. Klik op de knop om de demo te resetten
            en opnieuw te beginnen.
          </p>
        </div>

        {confirmReset ? (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-ink-4">Alle demo-gegevens worden gewist. Dit kan niet ongedaan worden gemaakt.</p>
            <div className="flex gap-2">
              <button onClick={handleDemoReset} disabled={resetting}
                className="flex-1 rounded-xl bg-moss text-white text-sm font-semibold py-3 hover:bg-moss-light disabled:opacity-50 transition-colors">
                {resetting ? 'Bezig...' : 'Bevestigen'}
              </button>
              <button onClick={() => setConfirmReset(false)} disabled={resetting}
                className="flex-1 rounded-xl border border-sand-300 text-ink-3 text-sm font-semibold py-3 hover:bg-sand-50 disabled:opacity-50 transition-colors">
                Annuleren
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setConfirmReset(true)}
            className="flex items-center justify-center gap-2 w-full rounded-xl bg-moss text-white text-sm font-semibold py-3.5 hover:bg-moss-light active:scale-[0.98] transition-all">
            <Trash2 className="h-4 w-4" />
            Demo resetten
          </button>
        )}

        <button onClick={() => setLoginFailed(false)}
          className="text-xs text-ink-4 hover:text-ink-3 underline underline-offset-2 transition-colors">
          ← Terug
        </button>
      </div>
    )
  }

  // ── Inlogscherm ───────────────────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-2xl shadow-soft border border-sand-300 p-8">
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-ink">
          Welkom terug{firstName ? `, ${firstName}` : ''}
        </h1>
        <p className="text-sm text-ink-3 mt-1">
          Voer uw wachtwoord in om verder te gaan.
        </p>
        {DEMO_MODE && (
          <div className="mt-3 flex items-start gap-2 rounded-xl bg-status-warnBg border border-status-warnBorder px-3 py-2.5">
            <span className="text-sm flex-shrink-0">ℹ️</span>
            <p className="text-xs text-status-warnText">
              <strong>Demo-versie:</strong> gebruik het vooraf ingestelde demonstratiewachtwoord.
            </p>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-5">
          <div className="flex items-start gap-2.5 rounded-xl bg-status-alertBg border border-status-alertBorder px-4 py-3">
            <span className="text-sm flex-shrink-0">⚠️</span>
            <p className="text-xs text-status-alertText leading-relaxed">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="flex flex-col gap-5">
        <div className="relative">
          <Input
            label="Wachtwoord"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            autoFocus
          />
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3.5 top-[38px] text-ink-4 hover:text-ink-3 transition-colors"
            aria-label={showPassword ? 'Verberg wachtwoord' : 'Toon wachtwoord'}
          >
            {showPassword
              ? <EyeOff className="h-4 w-4" />
              : <Eye    className="h-4 w-4" />
            }
          </button>
        </div>

        <Button
          type="submit"
          fullWidth
          loading={isLoading}
          disabled={!password}
        >
          Inloggen
        </Button>
      </form>

      {/* Demo-reset altijd zichtbaar op het inlogscherm in DEMO_MODE */}
      {DEMO_MODE && (
        <div className="mt-4">
          <DemoResetButton />
        </div>
      )}

      <p className="mt-4 text-center text-xs text-ink-4">
        Bent u een nabestaande?{' '}
        <Link
          to={ROUTES.NEXT_OF_KIN_ENTRY}
          className="text-sage-600 underline-offset-2 hover:underline"
        >
          Toegang aanvragen
        </Link>
      </p>
    </div>
  )
}
